import type { ReactNode } from "react";
import { hasHttpStatus } from "@/lib/api-error";
import { useCarrierMe } from "@/api/carrier";
import { PortalState } from "@/components/portal-state";
import { Button } from "@/components/ui/button";
import { useSession } from "@/services/auth0/session";

interface ProtectedRouteProps {
  children: ReactNode;
  preserveContentOnTransientError?: boolean;
}

/**
 * Gate every screen behind a valid session. Until Auth0 is configured
 * (env vars present + SPA app provisioned), `isLoggedIn` will be false and
 * we render an unauthenticated landing inline rather than redirect — that
 * keeps the dev smoke loop simple (no infinite redirect loops while Auth0
 * is null).
 */
export function ProtectedRoute({
  children,
  preserveContentOnTransientError = false,
}: ProtectedRouteProps) {
  const { isLoading, isLoggedIn, login, switchAccount } = useSession();
  const carrierMe = useCarrierMe();

  if (isLoading || (isLoggedIn && carrierMe.isLoading)) {
    return <PortalState title="Loading carrier portal" loading />;
  }

  if (!isLoggedIn) {
    return (
      <PortalState
        title="EZX Carrier Portal"
        description={
          <p>
            Sign in to access trips, tasks, shipments, and your team. Auth0
            configuration is required for sign-in to work.
          </p>
        }
      >
        <Button type="button" onClick={() => login()} size="touch">
          Sign in
        </Button>
      </PortalState>
    );
  }

  const status = hasHttpStatus(carrierMe.error, 401)
    ? 401
    : hasHttpStatus(carrierMe.error, 403)
      ? 403
      : undefined;

  if (carrierMe.isError && status === 401) {
    return (
      <PortalState
        title="Sign in again"
        description={
          <p>We couldn’t verify your session. Sign in again to continue.</p>
        }
      >
        <div className="flex flex-col gap-2">
          <Button type="button" onClick={() => login()} size="touch">
            Sign in again
          </Button>
          <Button
            type="button"
            onClick={switchAccount}
            variant="outline"
            size="touch"
          >
            Sign in with another account
          </Button>
        </div>
      </PortalState>
    );
  }

  if (
    (carrierMe.isError && status !== 403) ||
    (!carrierMe.isError && !carrierMe.data)
  ) {
    const unavailable = (
      <PortalState
        title="Carrier portal unavailable"
        description={
          <p>We couldn’t check your carrier access. Try again in a moment.</p>
        }
      >
        <Button
          type="button"
          onClick={() => void carrierMe.refetch()}
          disabled={carrierMe.isFetching}
          size="touch"
        >
          {carrierMe.isFetching ? "Checking access…" : "Try again"}
        </Button>
      </PortalState>
    );
    return preserveContentOnTransientError &&
      carrierMe.data?.capabilities.canAccessCarrierWeb
      ? retainedContent(children, unavailable)
      : unavailable;
  }

  if (carrierMe.isError || !carrierMe.data?.capabilities.canAccessCarrierWeb) {
    return (
      <PortalState
        title="Carrier access required"
        description={
          <p>
            {status === 403
              ? "This carrier account is inactive or does not have access to this app."
              : "This account cannot access the carrier portal."}
          </p>
        }
      >
        <Button type="button" onClick={switchAccount} size="touch">
          Sign in with another account
        </Button>
      </PortalState>
    );
  }

  return preserveContentOnTransientError ? retainedContent(children) : children;
}

function retainedContent(children: ReactNode, fallback?: ReactNode) {
  return (
    <>
      {fallback}
      <div hidden={!!fallback}>{children}</div>
    </>
  );
}
